import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'

import { useToast } from '@/hooks'
import { useAccount } from '@/modules/account'
import { useUser } from '@/modules/auth'
import { TrialProjectLimitError } from '@/modules/errors'
import {
  type Project,
  useProjects,
  useProjectsLoaders,
  useProjectsMutating,
  useProjectsMutations,
} from '@/modules/projects'
import { captureException } from '@/services/monitoring/captureException'
import { eventTracker } from '@/services/tracking'
import { getProjectPath } from '@/utils'
import { toTrialCaseNamedProps } from './trialCaseNamedEvent'

// The trial allows one case, and creating one is not idempotent: a reload or a second tab
// must land on the case that already exists instead of failing.
const useGoToExistingCase = (goToCase: (project: Project) => void) => {
  const [existingCase] = useProjects()
  const { fetchProjects } = useProjectsLoaders()
  const [hasLookedUp, setHasLookedUp] = useState(false)
  const lookUpCases = useCallback(
    () =>
      fetchProjects(null, { allFolders: true }).then(() =>
        setHasLookedUp(true),
      ),
    [fetchProjects],
  )

  useEffect(() => {
    lookUpCases().catch(captureException)
  }, [lookUpCases])

  useEffect(() => {
    if (hasLookedUp && existingCase) goToCase(existingCase)
  }, [hasLookedUp, existingCase, goToCase])

  return lookUpCases
}

const useReportCaseNamed = () => {
  const account = useAccount()
  const user = useUser()
  return () => {
    if (!account) return
    eventTracker.trialCaseNamed(toTrialCaseNamedProps(account, user?.email))
  }
}

export const useOpenTrialCase = () => {
  const navigate = useNavigate()
  const { errorToast } = useToast()
  const { createProject } = useProjectsMutations()
  const isOpening = useProjectsMutating()
  const goToCase = useCallback(
    (project: Project) => navigate({ to: `${getProjectPath(project)}/files` }),
    [navigate],
  )
  const lookUpCases = useGoToExistingCase(goToCase)
  const reportCaseNamed = useReportCaseNamed()

  const handleFailure = (error: Error) => {
    if (error instanceof TrialProjectLimitError) {
      lookUpCases().catch(captureException)
      return
    }
    errorToast('We could not open the case', error)
  }

  const openNewCase = (project: Project) => {
    reportCaseNamed()
    goToCase(project)
  }

  const openCase = (name: string) =>
    createProject({ name }).then(openNewCase).catch(handleFailure)

  return { openCase, isOpening }
}
